<template>
  <div class="page-card">
    <div class="section-header">
      <div class="section-header-left">
        <div class="section-title">医院列表</div>
        <div class="search-bar">
          <input
            v-model.trim="searchCode"
            class="search-input"
            placeholder="医院编号"
            @keyup.enter="handleSearch"
          />
          <input
            v-model.trim="searchName"
            class="search-input"
            placeholder="医院名称"
            @keyup.enter="handleSearch"
          />
          <button
            class="search-btn search-btn-reset"
            type="button"
            @click="resetSearch"
          >
            重置
          </button>
          <button
            class="search-btn search-btn-query"
            type="button"
            :disabled="listLoading"
            @click="handleSearch"
          >
            查询
          </button>
        </div>
      </div>
      <div class="section-actions">
        <button class="add-btn" type="button" @click="openAddHospital">
          + 新增门诊
        </button>
      </div>
    </div>

    <div v-if="listLoading" class="list-loading">加载中...</div>
    <template v-else>
      <table class="hospital-table">
        <thead>
          <tr>
            <th class="col-code">医院编号</th>
            <th class="col-name">医院名称</th>
            <th class="col-address">详细地址</th>
            <th class="col-lng">经度</th>
            <th class="col-lat">纬度</th>
            <th class="col-regional">省份</th>
            <th class="col-area">所属地区</th>
            <th class="col-action">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td colspan="8" class="empty-cell">暂无数据</td>
          </tr>
          <tr v-for="item in rows" :key="item.hospitalId || item.hospitalCode">
            <td class="cell-code">{{ item.hospitalCode || "-" }}</td>
            <td class="cell-name">{{ item.hospitalName || "-" }}</td>
            <td class="col-address">
              <el-tooltip
                v-if="item.address"
                :content="item.address"
                placement="top"
                :open-delay="300"
              >
                <span class="address-text">{{ item.address }}</span>
              </el-tooltip>
              <span v-else>-</span>
            </td>
            <td>{{ formatCoord(item.longitude) }}</td>
            <td>{{ formatCoord(item.latitude) }}</td>
            <td>{{ item.regional || "-" }}</td>
            <td>{{ item.areaName || "-" }}</td>
            <td class="col-action">
              <div class="action-btns">
                <button
                  class="action-btn action-btn-add-doctor"
                  type="button"
                  @click="addDoctorRow(item)"
                >
                  新增医生
                </button>
                <button
                  class="action-btn action-btn-doctor-list"
                  type="button"
                  @click="openDoctorListRow(item)"
                >
                  医生列表
                </button>
                <button
                  class="action-btn action-btn-edit"
                  type="button"
                  @click="editRow(item)"
                >
                  修改
                </button>
                <button
                  class="action-btn action-btn-delete"
                  type="button"
                  :disabled="deletingCode === item.hospitalCode"
                  @click="deleteRow(item)"
                >
                  {{ deletingCode === item.hospitalCode ? "删除中..." : "删除" }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <el-pagination
        v-if="total > 0"
        class="hospital-pagination"
        small
        layout="total, prev, pager, next"
        :current-page="pageNum"
        :page-size="pageSize"
        :total="total"
        @current-change="onPageChange"
      />
    </template>

    <!-- 新增门诊弹框 -->
    <div
      v-if="showAddModal"
      class="modal-overlay"
      @click.self="closeAddModal"
    >
      <div class="modal modal-hospital-edit">
        <div class="modal-header">
          <span class="modal-title">新增门诊</span>
          <span class="modal-close" @click="closeAddModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">
                <span class="required-mark">*</span>医院编号
              </label>
              <input
                v-model.trim="addForm.hospitalCode"
                class="form-input"
                required
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                <span class="required-mark">*</span>医院名称
              </label>
              <input
                v-model.trim="addForm.hospitalName"
                class="form-input"
                required
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">详细地址</label>
            <input
              v-model.trim="addForm.address"
              class="form-input"
            />
          </div>
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">经度</label>
              <input
                v-model.trim="addForm.longitude"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label class="form-label">纬度</label>
              <input
                v-model.trim="addForm.latitude"
                class="form-input"
              />
            </div>
          </div>
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">省份</label>
              <input
                v-model.trim="addForm.regional"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label class="form-label">所属地区</label>
              <input
                v-model.trim="addForm.areaName"
                class="form-input"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="footer-btn footer-btn-cancel" type="button" @click="closeAddModal">
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingAdd || !isAddFormValid"
            @click="saveAdd"
          >
            {{ savingAdd ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 新增医生弹框 -->
    <div
      v-if="showDoctorModal"
      class="modal-overlay"
      @click.self="closeDoctorModal"
    >
      <div class="modal modal-hospital-edit">
        <div class="modal-header">
          <span class="modal-title">新增医生</span>
          <span class="modal-close" @click="closeDoctorModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">医院编号</label>
              <input
                v-model="doctorForm.hospitalCode"
                class="form-input"
                disabled
              />
            </div>
            <div class="form-group">
              <label class="form-label">医院名称</label>
              <input
                v-model="doctorForm.hospitalName"
                class="form-input"
                disabled
              />
            </div>
          </div>
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">
                <span class="required-mark">*</span>医生名称
              </label>
              <input
                v-model.trim="doctorForm.doctorName"
                class="form-input"
                placeholder="请输入医生名称"
                required
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                <span class="required-mark">*</span>联系方式
              </label>
              <input
                v-model.trim="doctorForm.phone"
                class="form-input"
                placeholder="请输入联系方式"
                required
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="footer-btn footer-btn-cancel" type="button" @click="closeDoctorModal">
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingDoctor || !isDoctorFormValid"
            @click="saveDoctor"
          >
            {{ savingDoctor ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 医生列表弹框 -->
    <div
      v-if="showDoctorListModal"
      class="modal-overlay"
      @click.self="closeDoctorListModal"
    >
      <div class="modal modal-doctor-list">
        <div class="modal-header">
          <span class="modal-title">
            医生列表
            <span v-if="doctorListHospital.hospitalName" class="modal-subtitle">
              - {{ doctorListHospital.hospitalName }}（{{ doctorListHospital.hospitalCode }}）
            </span>
          </span>
          <span class="modal-close" @click="closeDoctorListModal">×</span>
        </div>
        <div class="modal-body">
          <div v-if="doctorListLoading" class="doctor-list-loading">加载中...</div>
          <table v-else class="doctor-list-table">
            <thead>
              <tr>
                <th>医生名称</th>
                <th>联系方式</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!doctorListRows.length">
                <td colspan="2" class="empty-cell">暂无医生</td>
              </tr>
              <tr
                v-for="(doctor, index) in doctorListRows"
                :key="doctor.doctorId || 'doctor-' + index"
              >
                <td>{{ doctor.realName || "-" }}</td>
                <td>{{ doctor.phone || "-" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button
            class="footer-btn footer-btn-cancel"
            type="button"
            @click="closeDoctorListModal"
          >
            关闭
          </button>
        </div>
      </div>
    </div>

    <!-- 修改医院弹框 -->
    <div
      v-if="showEditModal"
      class="modal-overlay"
      @click.self="closeEditModal"
    >
      <div class="modal modal-hospital-edit">
        <div class="modal-header">
          <span class="modal-title">修改医院</span>
          <span class="modal-close" @click="closeEditModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">医院编号</label>
              <input
                v-model.trim="editForm.hospitalCode"
                class="form-input"
                disabled
              />
            </div>
            <div class="form-group">
              <label class="form-label"><span class="required-mark">*</span>医院名称</label>
              <input
                v-model.trim="editForm.hospitalName"
                class="form-input"
                required
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">详细地址</label>
            <input
              v-model.trim="editForm.address"
              class="form-input"
            />
          </div>
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">经度</label>
              <input
                v-model.trim="editForm.longitude"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label class="form-label">纬度</label>
              <input
                v-model.trim="editForm.latitude"
                class="form-input"
              />
            </div>
          </div>
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">省份</label>
              <input
                v-model.trim="editForm.regional"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label class="form-label">所属地区</label>
              <input
                v-model.trim="editForm.areaName"
                class="form-input"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="footer-btn footer-btn-cancel" type="button" @click="closeEditModal">
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingEdit"
            @click="saveEdit"
          >
            {{ savingEdit ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { addDoctor, listDoctors } from "@/api/doctor";
import {
  addHospital,
  deleteHospital,
  listAllHospitals,
  updateHospital,
} from "@/api/hospital";

const SUCCESS_CODE = 20000;

export default {
  name: "HospitalList",
  data() {
    return {
      listLoading: false,
      savingAdd: false,
      savingDoctor: false,
      savingEdit: false,
      deletingCode: "",
      showAddModal: false,
      showDoctorModal: false,
      showDoctorListModal: false,
      doctorListLoading: false,
      doctorListRows: [],
      doctorListHospital: {
        hospitalId: "",
        hospitalCode: "",
        hospitalName: "",
      },
      showEditModal: false,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      searchCode: "",
      searchName: "",
      rows: [],
      addForm: {
        hospitalCode: "",
        hospitalName: "",
        address: "",
        longitude: "",
        latitude: "",
        regional: "",
        areaName: "",
      },
      editForm: {
        hospitalId: "",
        hospitalCode: "",
        hospitalName: "",
        address: "",
        longitude: "",
        latitude: "",
        regional: "",
        areaName: "",
      },
      doctorForm: {
        hospitalId: "",
        hospitalCode: "",
        hospitalName: "",
        doctorName: "",
        phone: "",
      },
    };
  },
  computed: {
    isAddFormValid() {
      return Boolean(
        String(this.addForm.hospitalCode || "").trim() &&
          String(this.addForm.hospitalName || "").trim()
      );
    },
    isDoctorFormValid() {
      return Boolean(
        String(this.doctorForm.doctorName || "").trim() &&
          String(this.doctorForm.phone || "").trim()
      );
    },
  },
  mounted() {
    this.fetchHospitalList();
  },
  methods: {
    showMessage(type, text) {
      this.$message({ message: text, type });
    },
    isBizError(res) {
      return res && res.code != null && Number(res.code) !== SUCCESS_CODE;
    },
    formatCoord(value) {
      if (value == null || value === "") return "-";
      return String(value);
    },
    resolveListPayload(data) {
      if (Array.isArray(data)) {
        return { list: data, total: data.length };
      }
      if (!data || typeof data !== "object") {
        return { list: [], total: 0 };
      }
      const list = data.list || data.records || data.rows || [];
      const total = Number(data.total ?? list.length) || 0;
      return { list: Array.isArray(list) ? list : [], total };
    },
    mapHospitalRow(item) {
      if (!item || typeof item !== "object") return null;
      const hospitalId = String(item.hospitalId ?? "").trim();
      const hospitalCode = String(item.hospitalCode ?? "").trim();
      if (!hospitalId && !hospitalCode) return null;
      return {
        hospitalId,
        hospitalCode,
        hospitalName: String(item.hospitalName ?? "").trim(),
        address: String(item.address ?? "").trim(),
        longitude: item.longitude ?? "",
        latitude: item.latitude ?? "",
        regional: String(item.regional ?? "").trim(),
        areaName: String(item.areaName ?? "").trim(),
      };
    },
    mapHospitalRows(list) {
      if (!Array.isArray(list)) return [];
      return list.map((item) => this.mapHospitalRow(item)).filter(Boolean);
    },
    async fetchHospitalList() {
      this.listLoading = true;
      try {
        const res = await listAllHospitals({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          hospitalCode: this.searchCode,
          hospitalName: this.searchName,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医院列表加载失败");
          this.rows = [];
          this.total = 0;
          return;
        }
        const payload = this.resolveListPayload(res && res.data);
        this.rows = this.mapHospitalRows(payload.list);
        this.total = payload.total;
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医院列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.rows = [];
        this.total = 0;
      } finally {
        this.listLoading = false;
      }
    },
    onPageChange(page) {
      this.pageNum = page;
      this.fetchHospitalList();
    },
    handleSearch() {
      this.pageNum = 1;
      this.fetchHospitalList();
    },
    resetSearch() {
      this.searchCode = "";
      this.searchName = "";
      this.pageNum = 1;
      this.fetchHospitalList();
    },
    resetAddForm() {
      this.addForm = {
        hospitalCode: "",
        hospitalName: "",
        address: "",
        longitude: "",
        latitude: "",
        regional: "",
        areaName: "",
      };
    },
    openAddHospital() {
      this.resetAddForm();
      this.showAddModal = true;
    },
    closeAddModal() {
      this.showAddModal = false;
    },
    async saveAdd() {
      const {
        hospitalCode,
        hospitalName,
        address,
        longitude,
        latitude,
        regional,
        areaName,
      } = this.addForm;
      if (!hospitalCode) {
        this.showMessage("warning", "请填写医院编号");
        return;
      }
      if (!hospitalName) {
        this.showMessage("warning", "请填写医院名称");
        return;
      }

      this.savingAdd = true;
      try {
        const res = await addHospital({
          hospitalCode,
          hospitalName,
          address,
          longitude,
          latitude,
          regional,
          areaName,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "门诊新增失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "门诊新增成功");
        this.closeAddModal();
        this.pageNum = 1;
        await this.fetchHospitalList();
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "门诊新增失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingAdd = false;
      }
    },
    resetDoctorForm() {
      this.doctorForm = {
        hospitalId: "",
        hospitalCode: "",
        hospitalName: "",
        doctorName: "",
        phone: "",
      };
    },
    addDoctorRow(item) {
      if (!item || !item.hospitalId) {
        this.showMessage("warning", "医院信息无效");
        return;
      }
      this.doctorForm = {
        hospitalId: item.hospitalId,
        hospitalCode: item.hospitalCode || "",
        hospitalName: item.hospitalName || "",
        doctorName: "",
        phone: "",
      };
      this.showDoctorModal = true;
    },
    closeDoctorModal() {
      this.showDoctorModal = false;
      this.resetDoctorForm();
    },
    async saveDoctor() {
      const { hospitalId, doctorName, phone } = this.doctorForm;
      if (!hospitalId) {
        this.showMessage("warning", "医院信息无效");
        return;
      }
      if (!doctorName) {
        this.showMessage("warning", "请填写医生名称");
        return;
      }
      if (!phone) {
        this.showMessage("warning", "请填写联系方式");
        return;
      }

      this.savingDoctor = true;
      try {
        const res = await addDoctor({
          hospitalId,
          doctorName,
          phone,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医生新增失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "医生新增成功");
        this.closeDoctorModal();
        if (
          this.showDoctorListModal &&
          this.doctorListHospital.hospitalId === hospitalId
        ) {
          await this.fetchDoctorList(hospitalId);
        }
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医生新增失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingDoctor = false;
      }
    },
    async openDoctorListRow(item) {
      if (!item || !item.hospitalId) {
        this.showMessage("warning", "医院信息无效");
        return;
      }
      this.doctorListHospital = {
        hospitalId: item.hospitalId,
        hospitalCode: item.hospitalCode || "",
        hospitalName: item.hospitalName || "",
      };
      this.showDoctorListModal = true;
      await this.fetchDoctorList(item.hospitalId);
    },
    closeDoctorListModal() {
      this.showDoctorListModal = false;
      this.doctorListRows = [];
      this.doctorListHospital = {
        hospitalId: "",
        hospitalCode: "",
        hospitalName: "",
      };
    },
    resolveDoctorListPayload(data) {
      if (Array.isArray(data)) return data;
      if (!data || typeof data !== "object") return [];
      if (Array.isArray(data.list)) return data.list;
      if (Array.isArray(data.records)) return data.records;
      if (Array.isArray(data.rows)) return data.rows;
      return [];
    },
    mapDoctorRow(item) {
      if (!item || typeof item !== "object") return null;
      const realName = String(item.realName ?? item.doctorName ?? "").trim();
      const phone = String(item.phone ?? "").trim();
      const doctorId = String(item.doctorId ?? item.id ?? "").trim();
      if (!realName && !phone) return null;
      return { doctorId, realName, phone };
    },
    mapDoctorRows(list) {
      if (!Array.isArray(list)) return [];
      return list.map((item) => this.mapDoctorRow(item)).filter(Boolean);
    },
    async fetchDoctorList(hospitalId) {
      this.doctorListLoading = true;
      try {
        const res = await listDoctors({ hospitalId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医生列表加载失败");
          this.doctorListRows = [];
          return;
        }
        const list = this.resolveDoctorListPayload(res && res.data);
        this.doctorListRows = this.mapDoctorRows(list);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医生列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.doctorListRows = [];
      } finally {
        this.doctorListLoading = false;
      }
    },
    editRow(item) {
      if (!item || !item.hospitalId) {
        this.showMessage("warning", "医院信息无效");
        return;
      }
      this.editForm = {
        hospitalId: item.hospitalId || "",
        hospitalCode: item.hospitalCode,
        hospitalName: item.hospitalName || "",
        address: item.address || "",
        longitude: item.longitude != null ? String(item.longitude) : "",
        latitude: item.latitude != null ? String(item.latitude) : "",
        regional: item.regional || "",
        areaName: item.areaName || "",
      };
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    async saveEdit() {
      const {
        hospitalId,
        hospitalCode,
        hospitalName,
        address,
        longitude,
        latitude,
        regional,
        areaName,
      } = this.editForm;
      if (!hospitalId) {
        this.showMessage("warning", "医院信息无效");
        return;
      }
      if (!hospitalCode) {
        this.showMessage("warning", "医院编号无效");
        return;
      }
      if (!hospitalName) {
        this.showMessage("warning", "请填写医院名称");
        return;
      }

      this.savingEdit = true;
      try {
        const res = await updateHospital({
          hospitalId,
          hospitalCode,
          hospitalName,
          address,
          longitude,
          latitude,
          regional,
          areaName,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医院修改失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "医院修改成功");
        this.closeEditModal();
        await this.fetchHospitalList();
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医院修改失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingEdit = false;
      }
    },
    deleteRow(item) {
      if (!item || !item.hospitalCode) {
        this.showMessage("warning", "医院信息无效");
        return;
      }

      const label = item.hospitalName
        ? `「${item.hospitalName}」`
        : `「${item.hospitalCode}」`;

      this.$confirm(`确认删除医院${label}？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          this.deletingCode = item.hospitalCode;
          try {
            const res = await deleteHospital({
              hospitalId: item.hospitalId,
            });
            if (this.isBizError(res)) {
              this.showMessage("error", res.msg || "医院删除失败");
              return;
            }
            this.showMessage("success", (res && res.msg) || "医院已删除");
            if (this.rows.length === 1 && this.pageNum > 1) {
              this.pageNum -= 1;
            }
            await this.fetchHospitalList();
          } catch (error) {
            const msg =
              (error && error.msg) ||
              error.message ||
              "医院删除失败，请稍后重试";
            this.showMessage("error", msg);
          } finally {
            this.deletingCode = "";
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.page-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.search-input {
  width: 140px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.search-input::placeholder {
  color: #bfbfbf;
}

.search-btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.search-btn-reset {
  background: #fff;
  color: #5c6b7f;
  border: 1px solid #d9d9d9;
}

.search-btn-reset:hover {
  color: #409eff;
  border-color: #409eff;
}

.search-btn-query {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.search-btn-query:hover:not(:disabled) {
  background: #096dd9;
  border-color: #096dd9;
}

.search-btn-query:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.add-btn {
  border: none;
  background: #409eff;
  color: #fff;
  border-radius: 4px;
  height: 32px;
  padding: 0 14px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.add-btn:hover {
  background: #096dd9;
}

.list-loading,
.empty-cell {
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.hospital-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  border: 1px solid #eef1f6;
}

.hospital-table th,
.hospital-table td {
  border-bottom: 1px solid #eef1f6;
  padding: 12px 14px;
  font-size: 14px;
  color: #3f4a5d;
  vertical-align: middle;
  text-align: left;
}

.hospital-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
  white-space: nowrap;
}

.hospital-table tbody tr:hover td {
  background: #f0f7ff;
}

.hospital-table .col-code {
  width: 90px;
}

.hospital-table .col-name {
  min-width: 120px;
}

.hospital-table .col-address {
  max-width: 220px;
}

.hospital-table .col-lng,
.hospital-table .col-lat {
  width: 90px;
  white-space: nowrap;
}

.hospital-table .col-regional {
  width: 80px;
}

.hospital-table .col-area {
  min-width: 100px;
}

.hospital-table .col-action {
  width: 320px;
  text-align: center;
}

.cell-code {
  font-weight: 700;
  color: #2f3a4c;
}

.cell-name {
  font-weight: 500;
  color: #2f3a4c;
}

.address-text {
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.action-btns {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

.action-btn {
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.action-btn-add-doctor {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.action-btn-add-doctor:hover {
  background: #096dd9;
  border-color: #096dd9;
}

.action-btn-doctor-list {
  background: #fff;
  color: #389e0d;
  border: 1px solid #b7eb8f;
}

.action-btn-doctor-list:hover {
  color: #237804;
  border-color: #73d13d;
  background: #f6ffed;
}

.action-btn-edit {
  background: #fff;
  color: #5c6b7f;
  border: 1px solid #d9d9d9;
}

.action-btn-edit:hover {
  color: #409eff;
  border-color: #409eff;
}

.action-btn-delete {
  background: #ff4d4f;
  color: #fff;
  border: 1px solid #ff4d4f;
}

.action-btn-delete:hover {
  background: #ff7875;
  border-color: #ff7875;
}

.action-btn-delete:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.hospital-pagination {
  margin-top: 16px;
  text-align: right;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.modal-hospital-edit,
.modal-doctor-list {
  width: 640px;
  max-width: calc(100% - 32px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: #666;
}

.doctor-list-loading {
  padding: 32px 0;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.doctor-list-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #eef1f6;
}

.doctor-list-table th,
.doctor-list-table td {
  border-bottom: 1px solid #eef1f6;
  padding: 12px 14px;
  font-size: 14px;
  color: #3f4a5d;
  text-align: left;
}

.doctor-list-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
}

.doctor-list-table tbody tr:hover td {
  background: #f0f7ff;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  cursor: pointer;
  font-size: 20px;
  color: #999;
  line-height: 1;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row-2 .form-group {
  flex: 1;
  min-width: 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.required-mark {
  color: #ff4d4f;
  margin-right: 4px;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  color: #333;
}

.form-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.form-input:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.modal-footer {
  padding: 12px 20px 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.footer-btn {
  min-width: 72px;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.footer-btn-cancel {
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;
}

.footer-btn-cancel:hover {
  color: #409eff;
  border-color: #409eff;
}

.footer-btn-save {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.footer-btn-save:hover {
  background: #096dd9;
  border-color: #096dd9;
}

.footer-btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
